import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  HandCoins, 
  FileCheck, 
  Phone, 
  ClipboardCheck, 
  BadgePoundSterling,
  Mail,
  Clock,
  CheckCircle2,
  Upload,
  X,
  Loader2,
  Camera,
  Car,
  User,
  AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { DEALERSHIP_INFO } from '../config/dealership';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cloudinary config - using unsigned upload
const CLOUDINARY_CLOUD_NAME = 'dieapbotq';
const CLOUDINARY_UPLOAD_PRESET = 'sell-car-uploads';

// Photo upload slots configuration
const PHOTO_SLOTS = [
  { key: 'photo_front_url', label: 'Front of car', required: false },
  { key: 'photo_driver_front_url', label: 'Driver front corner', required: false },
  { key: 'photo_driver_rear_url', label: 'Driver rear corner', required: false },
  { key: 'photo_rear_url', label: 'Rear of car', required: false },
  { key: 'photo_passenger_rear_url', label: 'Passenger rear corner', required: false },
  { key: 'photo_passenger_front_url', label: 'Passenger front corner', required: false },
  { key: 'photo_dashboard_url', label: 'Dashboard', required: false },
  { key: 'photo_interior_url', label: 'Interior', required: false },
  { key: 'photo_extra_url', label: 'Extra (optional)', required: false },
];

// Allowed image MIME types
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/webp'
];

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Photo Upload Component
const PhotoUploadSlot = ({ slot, value, onChange, uploading, setUploading }) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear previous error
    setError(null);

    // Validate file type
    const fileType = file.type.toLowerCase();
    if (!ALLOWED_IMAGE_TYPES.includes(fileType)) {
      const errorMsg = 'File type not supported. Please upload a JPG, PNG, or WebP image.';
      setError(errorMsg);
      toast.error(errorMsg);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const errorMsg = `Image too large. Max size is ${MAX_FILE_SIZE_MB}MB.`;
      setError(errorMsg);
      toast.error(errorMsg);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);
    setUploading(prev => ({ ...prev, [slot.key]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'sell_car_leads');

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Cloudinary error response:', errorData);
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      const data = await response.json();
      onChange(slot.key, data.secure_url);
      setError(null);
      toast.success(`${slot.label} uploaded`);
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = `Failed to upload ${slot.label}. Please try again.`;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
      setUploading(prev => ({ ...prev, [slot.key]: false }));
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    onChange(slot.key, null);
    setError(null);
  };

  return (
    <div className="relative" data-testid={`photo-slot-${slot.key}`}>
      <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
        {slot.label}
      </label>
      
      {value ? (
        // Show preview
        <div className="relative aspect-[4/3] bg-[#16202A] border border-white/10 overflow-hidden group">
          <img
            src={value}
            alt={slot.label}
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-500 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`remove-photo-${slot.key}`}
          >
            <X size={16} />
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 py-1 px-2">
            <p className="text-[10px] text-white truncate">{slot.label}</p>
          </div>
        </div>
      ) : (
        // Show upload button
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full aspect-[4/3] border-2 border-dashed border-white/20 hover:border-[#48CCCD]/50 flex flex-col items-center justify-center gap-2 transition-colors bg-[#16202A]/50 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`upload-btn-${slot.key}`}
        >
          {isUploading ? (
            <Loader2 size={24} className="text-[#48CCCD] animate-spin" />
          ) : (
            <>
              <Camera size={24} className={error ? "text-red-400" : "text-[#94A3B8]"} />
              <span className={`text-xs ${error ? "text-red-400" : "text-[#94A3B8]"}`}>Upload</span>
            </>
          )}
        </button>
      )}
      
      {/* Error message display */}
      {error && (
        <p className="text-red-400 text-[10px] mt-1 leading-tight" data-testid={`error-${slot.key}`}>
          {error}
        </p>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

const SellYourCarPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    // Car Details
    reg_number: '',
    make: '',
    model: '',
    year: '',
    mileage_km: '',
    condition_rating: '',
    finance_outstanding: '',
    description: '',
    // Owner Details
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    owner_county: '',
    // Photos
    photo_front_url: null,
    photo_driver_front_url: null,
    photo_driver_rear_url: null,
    photo_rear_url: null,
    photo_passenger_rear_url: null,
    photo_passenger_front_url: null,
    photo_dashboard_url: null,
    photo_interior_url: null,
    photo_extra_url: null,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handlePhotoChange = (key, url) => {
    setFormData(prev => ({ ...prev, [key]: url }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.reg_number.trim()) newErrors.reg_number = 'Registration number is required';
    if (!formData.make.trim()) newErrors.make = 'Make is required';
    if (!formData.model.trim()) newErrors.model = 'Model is required';
    if (!formData.year) newErrors.year = 'Year is required';
    else if (formData.year < 1900 || formData.year > 2030) newErrors.year = 'Please enter a valid year';
    if (!formData.mileage_km) newErrors.mileage_km = 'Mileage is required';
    else if (formData.mileage_km < 0) newErrors.mileage_km = 'Mileage must be positive';
    if (!formData.condition_rating) newErrors.condition_rating = 'Condition rating is required';
    if (!formData.finance_outstanding) newErrors.finance_outstanding = 'Please select Yes or No';
    if (!formData.owner_name.trim()) newErrors.owner_name = 'Name is required';
    if (!formData.owner_phone.trim()) newErrors.owner_phone = 'Phone is required';
    if (!formData.owner_email.trim()) newErrors.owner_email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) newErrors.owner_email = 'Please enter a valid email';
    if (!formData.owner_county.trim()) newErrors.owner_county = 'County is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      document.querySelector(`[name="${firstErrorField}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Check if any uploads are in progress
    if (Object.values(uploading).some(v => v)) {
      toast.error('Please wait for photo uploads to complete');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        reg_number: formData.reg_number.trim(),
        make: formData.make.trim(),
        model: formData.model.trim(),
        year: parseInt(formData.year),
        mileage_km: parseInt(formData.mileage_km),
        condition_rating: formData.condition_rating,
        finance_outstanding: formData.finance_outstanding === 'yes',
        description: formData.description.trim() || null,
        owner_name: formData.owner_name.trim(),
        owner_phone: formData.owner_phone.trim(),
        owner_email: formData.owner_email.trim(),
        owner_county: formData.owner_county.trim(),
        photo_front_url: formData.photo_front_url,
        photo_driver_front_url: formData.photo_driver_front_url,
        photo_driver_rear_url: formData.photo_driver_rear_url,
        photo_rear_url: formData.photo_rear_url,
        photo_passenger_rear_url: formData.photo_passenger_rear_url,
        photo_passenger_front_url: formData.photo_passenger_front_url,
        photo_dashboard_url: formData.photo_dashboard_url,
        photo_interior_url: formData.photo_interior_url,
        photo_extra_url: formData.photo_extra_url,
      };

      const response = await fetch(`${API}/sell-your-car`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit your details');
      }

      setIsSubmitted(true);
      toast.success('Your details have been submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const processSteps = [
    {
      icon: FileCheck,
      title: 'Submit Your Details',
      description: 'Fill in your car details using our simple online form. Include make, model, mileage, and condition.',
    },
    {
      icon: Phone,
      title: 'We Contact You',
      description: 'Our team will reach out within 24 hours to discuss your vehicle and arrange an inspection.',
    },
    {
      icon: ClipboardCheck,
      title: 'Vehicle Inspection',
      description: 'Bring your car in for a quick, no-obligation inspection at our premises.',
    },
    {
      icon: BadgePoundSterling,
      title: 'Receive Your Offer',
      description: 'Get a fair, transparent offer on the spot. If you accept, we handle all the paperwork.',
    },
  ];

  const benefits = [
    'Fast, no-obligation valuation',
    'Fair, transparent offers',
    'We handle all paperwork',
    'Same-day payment available',
    'No hidden fees or charges',
    'Professional, friendly service',
  ];

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#020405] pt-20" data-testid="sell-your-car-page">
        <section className="py-20 md:py-28">
          <div className="max-w-2xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-[#0A0F14] border border-[#48CCCD]/30 p-8 md:p-12 text-center"
              data-testid="sell-success-message"
            >
              <div className="w-20 h-20 mx-auto flex items-center justify-center bg-[#48CCCD]/20 border border-[#48CCCD]/50 mb-6">
                <CheckCircle2 size={40} className="text-[#48CCCD]" />
              </div>
              
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
                Thank You!
              </h1>
              
              <p className="text-[#94A3B8] font-sans text-lg leading-relaxed mb-8">
                We've received your details and will contact you shortly about your car. 
                Our team typically responds within 24 hours.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 h-12 px-8 bg-[#48CCCD] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors"
                >
                  Return Home
                </Link>
                <Link
                  to="/cars"
                  className="inline-flex items-center justify-center gap-2 h-12 px-8 bg-transparent border-2 border-white/30 text-white font-bold uppercase tracking-widest text-xs hover:border-[#48CCCD] hover:text-[#48CCCD] transition-colors"
                >
                  Browse Our Cars
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020405] pt-20" data-testid="sell-your-car-page">
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#16202A] via-[#0A0F14] to-[#020405]" />
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#48CCCD]/5 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-[#48CCCD]/20">
                <HandCoins size={24} className="text-[#48CCCD]" />
              </div>
              <p className="text-[#48CCCD] font-sans text-xs uppercase tracking-[0.3em]">
                We Buy Cars
              </p>
            </div>
            
            <h1 
              className="font-serif text-4xl sm:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6"
              data-testid="sell-page-title"
            >
              Sell Your Car to
              <span className="text-[#48CCCD]"> CMAC & SON AUTOS</span>
            </h1>
            
            <p className="font-sans text-lg text-[#94A3B8] leading-relaxed mb-6">
              Get a fast, fair offer from a trusted local dealership. Fill in the form below 
              and we'll be in touch within 24 hours.
            </p>
            
            {/* Benefits */}
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {benefits.slice(0, 6).map((benefit, index) => (
                <li
                  key={benefit}
                  className="flex items-center gap-2 text-[#F8FAFC] text-xs font-sans"
                >
                  <CheckCircle2 size={14} className="text-[#48CCCD] shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>
      
      {/* Form Section */}
      <section className="py-12 md:py-16 bg-[#0A0F14]" data-testid="sell-form-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} className="space-y-10" data-testid="sell-car-form">
            
            {/* Section 1: Car Details */}
            <div className="bg-[#16202A]/50 border border-white/10 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#48CCCD]/20">
                  <Car size={20} className="text-[#48CCCD]" />
                </div>
                <div>
                  <h2 className="text-[#F8FAFC] font-serif text-xl font-bold">Car Details</h2>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans">
                    Tell us about your vehicle
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Reg Number */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Registration Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="reg_number"
                    value={formData.reg_number}
                    onChange={handleInputChange}
                    placeholder="e.g. 202D12345"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] uppercase ${errors.reg_number ? 'border-red-500' : ''}`}
                    data-testid="input-reg-number"
                  />
                  {errors.reg_number && <p className="text-red-400 text-xs mt-1">{errors.reg_number}</p>}
                </div>
                
                {/* Make */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Make <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleInputChange}
                    placeholder="e.g. BMW"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.make ? 'border-red-500' : ''}`}
                    data-testid="input-make"
                  />
                  {errors.make && <p className="text-red-400 text-xs mt-1">{errors.make}</p>}
                </div>
                
                {/* Model */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Model <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="e.g. 3 Series"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.model ? 'border-red-500' : ''}`}
                    data-testid="input-model"
                  />
                  {errors.model && <p className="text-red-400 text-xs mt-1">{errors.model}</p>}
                </div>
                
                {/* Year */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    placeholder="e.g. 2019"
                    min="1900"
                    max="2030"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.year ? 'border-red-500' : ''}`}
                    data-testid="input-year"
                  />
                  {errors.year && <p className="text-red-400 text-xs mt-1">{errors.year}</p>}
                </div>
                
                {/* Mileage */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Mileage (km) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    name="mileage_km"
                    value={formData.mileage_km}
                    onChange={handleInputChange}
                    placeholder="e.g. 50000"
                    min="0"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.mileage_km ? 'border-red-500' : ''}`}
                    data-testid="input-mileage"
                  />
                  {errors.mileage_km && <p className="text-red-400 text-xs mt-1">{errors.mileage_km}</p>}
                </div>
                
                {/* Condition */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Condition <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={formData.condition_rating}
                    onValueChange={(value) => handleSelectChange('condition_rating', value)}
                  >
                    <SelectTrigger 
                      className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 ${errors.condition_rating ? 'border-red-500' : ''}`}
                      data-testid="select-condition"
                    >
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#16202A] border-white/10">
                      <SelectItem value="excellent" className="text-[#F8FAFC]">Excellent</SelectItem>
                      <SelectItem value="good" className="text-[#F8FAFC]">Good</SelectItem>
                      <SelectItem value="average" className="text-[#F8FAFC]">Average</SelectItem>
                      <SelectItem value="poor" className="text-[#F8FAFC]">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.condition_rating && <p className="text-red-400 text-xs mt-1">{errors.condition_rating}</p>}
                </div>
                
                {/* Finance Outstanding */}
                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Outstanding Finance? <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="finance_outstanding"
                        value="yes"
                        checked={formData.finance_outstanding === 'yes'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#48CCCD] bg-[#0A0F14] border-white/20 focus:ring-[#48CCCD]"
                        data-testid="radio-finance-yes"
                      />
                      <span className="text-[#F8FAFC] text-sm">Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="finance_outstanding"
                        value="no"
                        checked={formData.finance_outstanding === 'no'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#48CCCD] bg-[#0A0F14] border-white/20 focus:ring-[#48CCCD]"
                        data-testid="radio-finance-no"
                      />
                      <span className="text-[#F8FAFC] text-sm">No</span>
                    </label>
                  </div>
                  {errors.finance_outstanding && <p className="text-red-400 text-xs mt-1">{errors.finance_outstanding}</p>}
                </div>
                
                {/* Description */}
                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Description <span className="text-[#94A3B8]">(optional)</span>
                  </label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Tell us about your car's history, any issues, recent work done, etc."
                    rows={3}
                    className="bg-[#0A0F14] border-white/10 text-[#F8FAFC] focus:border-[#48CCCD] resize-none"
                    data-testid="input-description"
                  />
                </div>
              </div>
            </div>
            
            {/* Section 2: Photos */}
            <div className="bg-[#16202A]/50 border border-white/10 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#48CCCD]/20">
                  <Camera size={20} className="text-[#48CCCD]" />
                </div>
                <div>
                  <h2 className="text-[#F8FAFC] font-serif text-xl font-bold">Photos</h2>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans">
                    Upload photos of your vehicle (optional but recommended)
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {PHOTO_SLOTS.map((slot) => (
                  <PhotoUploadSlot
                    key={slot.key}
                    slot={slot}
                    value={formData[slot.key]}
                    onChange={handlePhotoChange}
                    uploading={uploading}
                    setUploading={setUploading}
                  />
                ))}
              </div>
              
              <p className="text-xs text-[#94A3B8] mt-4">
                <AlertCircle size={12} className="inline mr-1" />
                Max 10MB per image. Supported formats: JPG, PNG, WebP
              </p>
            </div>
            
            {/* Section 3: Personal Details */}
            <div className="bg-[#16202A]/50 border border-white/10 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#48CCCD]/20">
                  <User size={20} className="text-[#48CCCD]" />
                </div>
                <div>
                  <h2 className="text-[#F8FAFC] font-serif text-xl font-bold">Your Details</h2>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans">
                    How can we contact you?
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="owner_name"
                    value={formData.owner_name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.owner_name ? 'border-red-500' : ''}`}
                    data-testid="input-owner-name"
                  />
                  {errors.owner_name && <p className="text-red-400 text-xs mt-1">{errors.owner_name}</p>}
                </div>
                
                {/* Phone */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="tel"
                    name="owner_phone"
                    value={formData.owner_phone}
                    onChange={handleInputChange}
                    placeholder="07700 000 000"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.owner_phone ? 'border-red-500' : ''}`}
                    data-testid="input-owner-phone"
                  />
                  {errors.owner_phone && <p className="text-red-400 text-xs mt-1">{errors.owner_phone}</p>}
                </div>
                
                {/* Email */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    name="owner_email"
                    value={formData.owner_email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.owner_email ? 'border-red-500' : ''}`}
                    data-testid="input-owner-email"
                  />
                  {errors.owner_email && <p className="text-red-400 text-xs mt-1">{errors.owner_email}</p>}
                </div>
                
                {/* County */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                    County <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="owner_county"
                    value={formData.owner_county}
                    onChange={handleInputChange}
                    placeholder="e.g. Dublin"
                    className={`bg-[#0A0F14] border-white/10 text-[#F8FAFC] h-12 focus:border-[#48CCCD] ${errors.owner_county ? 'border-red-500' : ''}`}
                    data-testid="input-owner-county"
                  />
                  {errors.owner_county && <p className="text-red-400 text-xs mt-1">{errors.owner_county}</p>}
                </div>
              </div>
            </div>
            
            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                disabled={isSubmitting || Object.values(uploading).some(v => v)}
                className="w-full sm:w-auto h-14 px-12 bg-[#48CCCD] text-black font-bold uppercase tracking-widest text-sm hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <HandCoins size={18} className="mr-2" />
                    Get Your Offer
                  </>
                )}
              </Button>
              
              <p className="text-xs text-[#94A3B8] mt-4">
                By submitting, you agree to be contacted about selling your car.
              </p>
            </div>
          </form>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="py-16 md:py-20 bg-[#020405]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#48CCCD] font-sans text-xs uppercase tracking-[0.3em] mb-4">
              Simple Process
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight">
              How It Works
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <div className="bg-[#16202A]/50 border border-white/10 p-6 h-full hover:border-[#48CCCD]/30 transition-all duration-300">
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-[#48CCCD] flex items-center justify-center">
                    <span className="font-serif text-sm font-bold text-black">
                      {index + 1}
                    </span>
                  </div>
                  
                  <div className="w-12 h-12 flex items-center justify-center bg-[#0A0F14] mb-4 mt-2">
                    <step.icon size={24} className="text-[#48CCCD]" strokeWidth={1.5} />
                  </div>
                  
                  <h3 className="font-serif text-lg font-bold text-[#F8FAFC] mb-2">
                    {step.title}
                  </h3>
                  <p className="font-sans text-sm text-[#94A3B8] leading-relaxed">
                    {step.description}
                  </p>
                </div>
                
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-[2px] bg-gradient-to-r from-[#48CCCD]/50 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Contact CTA Section */}
      <section className="py-16 md:py-20 bg-[#0A0F14]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#F8FAFC] tracking-tight mb-4">
              Prefer to Talk?
            </h2>
            <p className="font-sans text-[#94A3B8] text-base leading-relaxed mb-6">
              Give us a call or send an email and we'll be happy to discuss selling your car.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={DEALERSHIP_INFO.phoneHref}
                className="inline-flex items-center justify-center gap-2 h-12 px-8 bg-[#48CCCD] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors"
                data-testid="contact-phone-btn"
              >
                <Phone size={16} />
                {DEALERSHIP_INFO.phone}
              </a>
              <a
                href={DEALERSHIP_INFO.emailHref}
                className="inline-flex items-center justify-center gap-2 h-12 px-8 bg-transparent border-2 border-white/30 text-white font-bold uppercase tracking-widest text-xs hover:border-[#48CCCD] hover:text-[#48CCCD] transition-colors"
                data-testid="contact-email-btn"
              >
                <Mail size={16} />
                Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SellYourCarPage;
